import React, { useState, useEffect, useMemo } from "react";
import { formatCHF } from "../utils/formatUtils";
import ExpenseCategory from "./ExpenseCategory";
import ProjectIncome from "./ProjectIncome";
import {
  getBudgetTargets,
  saveBudgetTarget,
  getBudgetEntries,
} from "../services/azureBudgetService";
import { getBookings } from "../services/azureBookingService";
import { getAllProjects } from "../services/azureProjectsService";
import { getProjectPhases } from "../services/azureProjectPhasesService";

// Fixed expense categories
const EXPENSE_CATEGORIES = [
  "Personalaufwand",
  "Materialaufwand",
  "Raumaufwand",
  "Verwaltungsaufwand",
  "Informatikaufwand",
  "Lizenzen, Updates",
  "Büromaterial",
  "Fahrzeug und Transportaufwand",
  "Sachversicherungen, Abgaben, Gebühren, Bewilligungen",
  "Andere",
];

// Category mapping from account numbers (Konto)
const getCategoryFromKonto = (konto, kontoName = "") => {
  const kontoNum = parseInt(konto);
  const name = (kontoName || "").toLowerCase();

  // Ignore specific accounts
  if (kontoNum === 4014 || kontoNum === 4400) return null;

  // Personalaufwand (5000-5999), but exclude Verpflegungsspesen
  if (kontoNum >= 5000 && kontoNum < 6000) {
    // Verpflegungsspesen (58xx) goes to Verwaltungsaufwand
    if (kontoNum >= 5800 && kontoNum < 5900) return "Verwaltungsaufwand";
    return "Personalaufwand";
  }

  // Materialaufwand (4000-4999)
  if (kontoNum >= 4000 && kontoNum < 5000) return "Materialaufwand";

  // Raumaufwand (6000-6099), but 6100 goes to Informatikaufwand
  if (kontoNum >= 6000 && kontoNum < 6100) return "Raumaufwand";

  // Specific account mappings
  if (kontoNum === 6100) return "Informatikaufwand";
  if (kontoNum === 6130) return "Büromaterial";

  // Verwaltungsaufwand (6101-6299, excluding 6100 and 6130)
  if (kontoNum >= 6101 && kontoNum < 6300) {
    return "Verwaltungsaufwand";
  }

  // Informatikaufwand (6300-6499), but exclude Lizenzen
  if (kontoNum >= 6300 && kontoNum < 6500) {
    // Lizenzen, Updates (6580 or name contains "lizenz" or "update")
    if (kontoNum === 6580 || name.includes("lizenz") || name.includes("update")) {
      return "Lizenzen, Updates";
    }
    return "Informatikaufwand";
  }

  // Büromaterial (6500-6599, formerly "Unterhalt Reparaturen")
  if (kontoNum >= 6500 && kontoNum < 6600) {
    // Lizenzen in 65xx range
    if (kontoNum === 6580 || name.includes("lizenz") || name.includes("update")) {
      return "Lizenzen, Updates";
    }
    return "Büromaterial";
  }

  // Fahrzeug und Transportaufwand (6600-6699)
  if (kontoNum >= 6600 && kontoNum < 6700) return "Fahrzeug und Transportaufwand";

  // Sachversicherungen, Abgaben, Gebühren, Bewilligungen (6700-6899)
  if (kontoNum >= 6700 && kontoNum < 6900) return "Sachversicherungen, Abgaben, Gebühren, Bewilligungen";

  return "Andere";
};

// Helper: Calculate how many months of a phase fall within a year
const calculateMonthsInYear = (startDate, endDate, year) => {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  const phaseStart = new Date(startDate);
  const phaseEnd = new Date(endDate);

  // If phase doesn't overlap with year, return 0
  if (phaseEnd < yearStart || phaseStart > yearEnd) {
    return { monthsInYear: 0, totalMonths: 0 };
  }

  // Calculate total months of phase
  const totalMonths = Math.max(1,
    (phaseEnd.getFullYear() - phaseStart.getFullYear()) * 12 +
    (phaseEnd.getMonth() - phaseStart.getMonth()) + 1
  );

  // Calculate overlap with year
  const overlapStart = new Date(Math.max(phaseStart.getTime(), yearStart.getTime()));
  const overlapEnd = new Date(Math.min(phaseEnd.getTime(), yearEnd.getTime()));

  const monthsInYear = Math.max(1,
    (overlapEnd.getFullYear() - overlapStart.getFullYear()) * 12 +
    (overlapEnd.getMonth() - overlapStart.getMonth()) + 1
  );

  return { monthsInYear, totalMonths };
};

const BudgetPage = () => {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [availableYears] = useState(() => {
    const cur = new Date().getFullYear();
    return [cur - 1, cur, cur + 1];
  });

  // Data state
  const [budgetTargets, setBudgetTargets] = useState({});
  const [bookings, setBookings] = useState([]);
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectPhases, setProjectPhases] = useState({});

  // Load all data
  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [targetsData, bookingsData, budgetData, projectsData] = await Promise.all([
        getBudgetTargets(String(year)),
        getBookings(),
        getBudgetEntries(),
        getAllProjects(),
      ]);

      setBudgetTargets(targetsData);
      setBookings(bookingsData);
      setBudgetEntries(budgetData);
      setProjects(projectsData);

      // Load phases for all projects
      const phasesMap = {};
      for (const project of projectsData) {
        if (project.code) {
          try {
            const phases = await getProjectPhases(project.code);
            phasesMap[project.code] = phases;
          } catch (error) {
            console.warn(`Could not load phases for project ${project.code}`);
          }
        }
      }
      setProjectPhases(phasesMap);
    } catch (error) {
      console.error("Fehler beim Laden der Budget-Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate expense data by category with account breakdown
  const expenseData = useMemo(() => {
    const data = {};

    // Initialize all categories
    EXPENSE_CATEGORIES.forEach((cat) => {
      data[cat] = {
        budgetTarget: budgetTargets[cat] || 0,
        bezahlt: 0,
        budgetiert: 0,
        accounts: {}, // Account breakdown: { "5000": { name: "Löhne", bezahlt: 1000 } }
        budgetEntries: [], // Individual budget entries for debugging
      };
    });

    // Calculate paid amounts from bookings
    // Filter bookings by year (using Valuta date)
    const yearBookings = bookings.filter((b) => {
      const bookingDate = new Date(b.Valuta);
      return bookingDate.getFullYear() === year;
    });

    // Sum all bookings where Konto is in expense ranges (4000-6999)
    yearBookings.forEach((booking) => {
      const konto = booking.Konto;
      // Only process expense accounts (4000-6999)
      if (konto >= 4000 && konto < 7000) {
        const category = getCategoryFromKonto(konto, booking.KontoBezeichnung);
        if (data[category]) {
          const betrag = Math.abs(booking.Betrag || 0);
          data[category].bezahlt += betrag;

          // Track by account
          const kontoKey = String(konto);
          if (!data[category].accounts[kontoKey]) {
            data[category].accounts[kontoKey] = {
              nummer: konto,
              name: booking.KontoBezeichnung || `Konto ${konto}`,
              bezahlt: 0,
            };
          }
          data[category].accounts[kontoKey].bezahlt += betrag;
        }
      }
    });

    // Calculate budgeted amounts from budget entries
    const yearBudgetEntries = budgetEntries.filter((entry) => {
      if (entry.typ !== "Ausgabe") return false;
      if (entry.projekt) return false; // Exclude project-related expenses

      if (entry.typ_timing === "Einmalig") {
        const entryDate = new Date(entry.versand_datum || entry.wann);
        return entryDate.getFullYear() === year;
      } else {
        // For recurring, check if active in this year
        const startDate = new Date(entry.ab_wann);
        const endDate = entry.bis_wann ? new Date(entry.bis_wann) : new Date(year + 10, 11, 31);
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        return startDate <= yearEnd && endDate >= yearStart;
      }
    });

    // Get current date for calculating remaining periods
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth(); // 0-11
    const currentYear = today.getFullYear();

    yearBudgetEntries.forEach((entry) => {
      const category = getCategoryFromKonto(entry.konto);
      // Skip ignored accounts (null) and fallback to "Andere" for unknown
      if (category === null) return;
      const finalCategory = category || "Andere";
      if (data[finalCategory]) {
        const amount = entry.netto_betrag || entry.betrag || 0;
        let budgetedAmount = 0;

        if (entry.typ_timing === "Regelmässig") {
          // Calculate period range within the selected year
          const entryStart = entry.ab_wann ? new Date(entry.ab_wann) : new Date(year, 0, 1);
          const entryEnd = entry.bis_wann ? new Date(entry.bis_wann) : new Date(year, 11, 31);

          // If the entry's end date is in the past, skip it entirely
          if (entryEnd < today) {
            data[finalCategory].budgetEntries.push({
              ...entry,
              budgetedAmount: 0,
              skipped: true,
              reason: "End date in the past",
            });
            return;
          }

          // Get the payment day from wann or ab_wann
          const paymentDay = entry.wann
            ? new Date(entry.wann).getDate()
            : (entry.ab_wann ? new Date(entry.ab_wann).getDate() : 1);

          // Determine start and end months for this year
          const yearStartMonth = entryStart.getFullYear() === year ? entryStart.getMonth() : 0;
          const yearEndMonth = entryEnd.getFullYear() === year ? entryEnd.getMonth() : 11;

          if (entry.haeufigkeit === "monatlich") {
            // Monthly: count only future payment dates
            let futurePayments = 0;

            if (year < currentYear) {
              futurePayments = 0;
            } else if (year > currentYear) {
              futurePayments = yearEndMonth - yearStartMonth + 1;
            } else {
              // Current year: check if this month's payment has passed
              let startMonth = Math.max(currentMonth, yearStartMonth);

              // If payment day has passed this month, start from next month
              if (currentDay > paymentDay) {
                startMonth = currentMonth + 1;
              }

              futurePayments = Math.max(0, yearEndMonth - startMonth + 1);
            }

            budgetedAmount = amount * futurePayments;

            data[finalCategory].budgetiert += budgetedAmount;
            data[finalCategory].budgetEntries.push({
              ...entry,
              budgetedAmount,
              futureMonths: futurePayments,
              paymentDay,
            });

          } else if (entry.haeufigkeit === "quartalsweise") {
            // Quarterly: count future quarterly payment dates
            let futurePayments = 0;
            const qStartMonth = entry.ab_wann ? new Date(entry.ab_wann).getMonth() : 0;

            if (year < currentYear) {
              futurePayments = 0;
            } else if (year > currentYear) {
              // Count quarterly months that fall within this year's range
              for (let m = yearStartMonth; m <= yearEndMonth; m++) {
                if ((m - qStartMonth + 120) % 3 === 0) futurePayments++;
              }
            } else {
              for (let m = yearStartMonth; m <= yearEndMonth; m++) {
                if ((m - qStartMonth + 120) % 3 !== 0) continue;
                // Check if this quarterly payment is still in the future
                if (m > currentMonth || (m === currentMonth && currentDay <= paymentDay)) {
                  futurePayments++;
                }
              }
            }

            budgetedAmount = amount * futurePayments;

            data[finalCategory].budgetiert += budgetedAmount;
            data[finalCategory].budgetEntries.push({
              ...entry,
              budgetedAmount,
              futureMonths: futurePayments,
              paymentDay,
            });

          } else {
            // Yearly: check if this year's payment date has passed
            const paymentMonth = entry.wann
              ? new Date(entry.wann).getMonth()
              : (entry.ab_wann ? new Date(entry.ab_wann).getMonth() : 0);

            let isPastPayment = false;
            if (year < currentYear) {
              isPastPayment = true;
            } else if (year === currentYear) {
              // Check if payment date has passed
              if (currentMonth > paymentMonth ||
                  (currentMonth === paymentMonth && currentDay > paymentDay)) {
                isPastPayment = true;
              }
            }

            if (!isPastPayment) {
              budgetedAmount = amount;
            }

            data[finalCategory].budgetiert += budgetedAmount;
            data[finalCategory].budgetEntries.push({
              ...entry,
              budgetedAmount,
              paymentDay,
              paymentMonth,
              skipped: isPastPayment,
              reason: isPastPayment ? "Payment date passed" : undefined,
            });
          }
        } else {
          // One-time payment: only count if NOT paid (future expense)
          if (entry.status !== "paid") {
            budgetedAmount = amount;
            data[finalCategory].budgetiert += budgetedAmount;
          }

          // Store entry for debugging
          data[finalCategory].budgetEntries.push({
            ...entry,
            budgetedAmount,
            skipped: entry.status === "paid",
            reason: entry.status === "paid" ? "Already paid" : undefined,
          });
        }
      }
    });

    return data;
  }, [bookings, budgetEntries, budgetTargets, year]);

  // Calculate project income data with year-proportional budgets
  const projectData = useMemo(() => {
    const data = {};

    // Process each project with phases
    projects.forEach((project) => {
      if (!project.code) return;

      const phases = projectPhases[project.code] || [];
      if (phases.length === 0) return;

      let yearHonorar = 0;
      let yearDrittkosten = 0;
      let hasActivityInYear = false;

      // Calculate year-proportional budget for each phase
      phases.forEach((phase) => {
        if (!phase.startDate || !phase.endDate) return;

        const { monthsInYear, totalMonths } = calculateMonthsInYear(
          phase.startDate,
          phase.endDate,
          year
        );

        if (monthsInYear > 0) {
          hasActivityInYear = true;
          const proportion = monthsInYear / totalMonths;
          yearHonorar += (phase.budget || 0) * proportion;
          yearDrittkosten += (phase.externalBudget || 0) * proportion;
        }
      });

      if (!hasActivityInYear) return;

      // Calculate bills for this year
      // Count as "bezahlt" if:
      // - versand_datum is in the past, OR
      // - status is "paid", OR
      // - status is "overdue" (always considered paid)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const projectBills = budgetEntries.filter((entry) => {
        if (entry.projekt !== project.code) return false;

        // Check versand_datum (send date) for year
        // wann = Rechnungsdatum (invoice date), same as versand_datum
        let sendDate;
        if (entry.versand_datum) {
          sendDate = new Date(entry.versand_datum);
        } else if (entry.wann) {
          sendDate = new Date(entry.wann);
        } else {
          return false;
        }

        if (sendDate.getFullYear() !== year) return false;

        // Count as paid if versand_datum is in the past OR status is "paid" OR status is "overdue"
        const isPaid = sendDate <= today || entry.status === "paid" || entry.status === "overdue";
        return isPaid;
      });

      // Separate income and expense bills (use netto, excluding MwSt)
      const payments = projectBills.map((entry) => ({
        id: entry.RowKey || entry.id,
        beschreibung: entry.bezeichnung || entry.beschreibung || entry.name || `${entry.typ} ${project.code}`,
        betrag: entry.netto_betrag || entry.betrag || 0,
        datum: entry.versand_datum || entry.wann,
        typ: entry.typ, // "Einnahme" or "Ausgabe"
      }));

      const bezahlt = payments
        .filter((p) => p.typ === "Einnahme")
        .reduce((sum, p) => sum + p.betrag, 0);

      data[project.code] = {
        code: project.code,
        name: project.title || project.longtitle || "Unbekannt",
        budget: Math.round(yearHonorar),
        drittkosten: Math.round(yearDrittkosten),
        bezahlt: bezahlt,
        payments: payments,
      };
    });

    return data;
  }, [budgetEntries, projects, projectPhases, year]);

  // Calculate global max value for scaling all project bars
  // Use max of (budget, bezahltIncome + ausgaben) across all projects
  const maxProjectValue = useMemo(() => {
    const values = Object.values(projectData).flatMap((p) => {
      const payments = p.payments || [];
      const bezahltIncome = payments
        .filter((pay) => pay.typ === "Einnahme")
        .reduce((sum, pay) => sum + pay.betrag, 0);
      const ausgaben = payments
        .filter((pay) => pay.typ === "Ausgabe")
        .reduce((sum, pay) => sum + pay.betrag, 0);
      return [p.budget, bezahltIncome + ausgaben];
    });
    return Math.max(...values, 1);
  }, [projectData]);

  // Calculate global max value for scaling all expense bars
  // Use max of (budgetTarget, bezahlt+budgetiert) for each category
  const maxExpenseValue = useMemo(() => {
    const values = Object.values(expenseData).flatMap((e) => [
      e.budgetTarget,
      e.bezahlt + e.budgetiert,
    ]);
    return Math.max(...values, 1);
  }, [expenseData]);

  // Calculate income totals (Eigenanteil = budget - drittkosten)
  const incomeTotals = useMemo(() => {
    let budgetiert = 0; // SUM(Eigenanteil Budget)
    let effektiv = 0; // SUM(Einnahmen = Bezahlt - Ausgaben)
    let umsatz = 0; // SUM(all income payments)
    let umsatzBudgetiert = 0; // SUM(all project budgets)

    Object.values(projectData).forEach((proj) => {
      const eigenanteilBudget = proj.budget - (proj.drittkosten || 0);
      budgetiert += eigenanteilBudget;
      umsatzBudgetiert += proj.budget || 0;

      // Calculate einnahmen = bezahltIncome - ausgaben
      const payments = proj.payments || [];
      const bezahltIncome = payments
        .filter((p) => p.typ === "Einnahme")
        .reduce((sum, p) => sum + p.betrag, 0);
      const ausgaben = payments
        .filter((p) => p.typ === "Ausgabe")
        .reduce((sum, p) => sum + p.betrag, 0);
      const einnahmen = bezahltIncome - ausgaben;
      effektiv += einnahmen;
      umsatz += bezahltIncome;
    });

    return { budgetiert, effektiv, umsatz, umsatzBudgetiert };
  }, [projectData]);

  // Calculate expense totals
  const expenseTotals = useMemo(() => {
    let budgetTarget = 0;
    let verbraucht = 0; // bezahlt + budgetiert (from Budget Verwaltung)

    Object.values(expenseData).forEach((cat) => {
      budgetTarget += cat.budgetTarget || 0;
      verbraucht += (cat.bezahlt || 0) + (cat.budgetiert || 0);
    });

    return { budgetiert: budgetTarget, effektiv: verbraucht };
  }, [expenseData]);



  const handleBudgetChange = async (category, amount) => {
    try {
      await saveBudgetTarget(String(year), category, amount);
      setBudgetTargets((prev) => ({
        ...prev,
        [category]: amount,
      }));
    } catch (error) {
      console.error("Fehler beim Speichern des Budget-Zielwerts:", error);
      alert("Fehler beim Speichern des Budgets.");
    }
  };

  if (loading) {
    return (
      <div className="budget-page">
        <div className="budget-loading">Lade Budget-Daten...</div>
      </div>
    );
  }

  return (
    <div className="budget-page">
      {/* Year selector */}
      <div className="budget-year-selector">
        <div className="year-tabs">
          {availableYears.map((y) => (
            <button
              key={y}
              className={`year-tab ${year === y ? "active" : ""}`}
              onClick={() => setYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="budget-columns">
        {/* Left column: Project Income (Einnahmen) */}
        <div className="budget-column income">
          <div className="column-header">
            <h2>Einnahmen</h2>
            <div className="column-totals">
              <div className="total-row">
                <span className="total-label">Eigenanteil budgetiert:</span>
                <span className="total-value">CHF {formatCHF(incomeTotals.budgetiert)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">Eigenanteil effektiv:</span>
                <span className="total-value">{incomeTotals.effektiv >= 0 ? '' : '-'}CHF {formatCHF(Math.abs(incomeTotals.effektiv))}</span>
              </div>
              <div className="total-row separator">
                <span className="total-label">Umsatz budgetiert:</span>
                <span className="total-value">CHF {formatCHF(incomeTotals.umsatzBudgetiert)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">Umsatz effektiv:</span>
                <span className="total-value">CHF {formatCHF(incomeTotals.umsatz)}</span>
              </div>
            </div>
          </div>
          <div className="project-incomes">
            {Object.keys(projectData).length === 0 ? (
              <div className="no-projects">
                Keine Projekte mit Aktivität in {year}
              </div>
            ) : (
              Object.values(projectData)
                .sort((a, b) => {
                  // Sort by highest value (max of budget or paid income)
                  const aMax = Math.max(a.budget, a.bezahlt || 0);
                  const bMax = Math.max(b.budget, b.bezahlt || 0);
                  return bMax - aMax;
                })
                .map((proj) => (
                  <ProjectIncome
                    key={proj.code}
                    projekt={{ code: proj.code, name: proj.name }}
                    budget={proj.budget}
                    drittkosten={proj.drittkosten}
                    bezahlt={proj.bezahlt}
                    payments={proj.payments || []}
                    maxValue={maxProjectValue}
                  />
                ))
            )}
          </div>
        </div>

        {/* Right column: Expenses (Ausgaben) */}
        <div className="budget-column expenses">
          <div className="column-header">
            <h2>Ausgaben</h2>
            <div className="column-totals">
              <div className="total-row">
                <span className="total-label">Ausgaben budgetiert:</span>
                <span className="total-value">CHF {formatCHF(expenseTotals.budgetiert)}</span>
              </div>
              <div className="total-row">
                <span className="total-label">Ausgaben effektiv:</span>
                <span className="total-value">CHF {formatCHF(expenseTotals.effektiv)}</span>
              </div>
              {/* Empty rows to align with Einnahmen section */}
              <div className="total-row separator"></div>
              <div className="total-row"></div>
            </div>
          </div>
          <div className="expense-categories">
            {EXPENSE_CATEGORIES
              .map((cat) => ({
                name: cat,
                ...expenseData[cat],
              }))
              .sort((a, b) => {
                // Sort by highest value (max of budgetTarget or bezahlt)
                const aMax = Math.max(a.budgetTarget || 0, a.bezahlt || 0);
                const bMax = Math.max(b.budgetTarget || 0, b.bezahlt || 0);
                return bMax - aMax;
              })
              .map((cat) => (
                <ExpenseCategory
                  key={cat.name}
                  kategorie={cat.name}
                  budgetTarget={cat.budgetTarget || 0}
                  bezahlt={cat.bezahlt || 0}
                  budgetiert={cat.budgetiert || 0}
                  accounts={cat.accounts || {}}
                  budgetEntries={cat.budgetEntries || []}
                  onBudgetChange={handleBudgetChange}
                  maxValue={maxExpenseValue}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetPage;
